import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import EditorLayout from "@/components/editor/EditorLayout";
import BlockForm from "@/components/editor/BlockForm";
import { BLOCKS, BLOCK_ORDER, ES_BLOCK_ORDER, newBlock, validateBlock } from "@/blocks/registry";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  Save, Eye, Globe, MoreVertical, Trash2, History, ChevronUp, ChevronDown, Plus,
  ArrowRight, GripVertical, Settings2, AlertTriangle, RotateCcw, Archive, CloudOff, X,
} from "lucide-react";
import { toast } from "sonner";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

export default function PageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [expanded, setExpanded] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delAck, setDelAck] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [blockDelIdx, setBlockDelIdx] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [saveReminderOpen, setSaveReminderOpen] = useState(false);
  const reminderRef = useRef(null);

  const scheduleReminder = () => {
    if (reminderRef.current) clearTimeout(reminderRef.current);
    reminderRef.current = setTimeout(() => setSaveReminderOpen(true), 120000);
  };
  const clearReminder = () => { if (reminderRef.current) clearTimeout(reminderRef.current); reminderRef.current = null; };
  useEffect(() => () => clearReminder(), []);

  // Warn on tab close / refresh when there are unsaved changes.
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/pages/${id}`);
      setPage(r.data);
    } catch { navigate("/editor"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const patch = (updates) => { setPage((p) => ({ ...p, ...updates })); setDirty(true); scheduleReminder(); };
  const setBlock = (i, nb) => { const blocks = page.blocks.map((b, idx) => (idx === i ? nb : b)); patch({ blocks }); };
  const addBlock = (type) => { patch({ blocks: [...page.blocks, newBlock(type)] }); setExpanded(page.blocks.length); setAddOpen(false); };
  const removeBlock = (i) => { patch({ blocks: page.blocks.filter((_, idx) => idx !== i) }); };
  const moveBlock = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= page.blocks.length) return;
    const blocks = [...page.blocks]; [blocks[i], blocks[j]] = [blocks[j], blocks[i]]; patch({ blocks });
  };

  const validateAll = () => {
    const errs = [];
    if (!page.title.trim()) errs.push("חסר שם לעמוד");
    if (!page.slug.trim()) errs.push("חסרה כתובת (slug)");
    page.blocks.forEach((b) => validateBlock(b).forEach((e) => errs.push(`${BLOCKS[b.type]?.label}: ${e}`)));
    return errs;
  };

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const r = await api.put(`/pages/${id}`, {
        title: page.title, slug: page.slug, blocks: page.blocks, seo: page.seo,
        show_in_nav: page.show_in_nav, nav_order: page.nav_order,
      });
      setPage(r.data); setDirty(false); clearReminder(); setSaveReminderOpen(false);
      if (!silent) toast.success("הטיוטה נשמרה");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail === "Slug already exists" ? "הכתובת (slug) כבר קיימת בעמוד אחר" : (err.response?.data?.detail || "השמירה נכשלה"));
      return false;
    } finally { setSaving(false); }
  };

  const goBack = () => { if (dirty) setLeaveOpen(true); else navigate("/editor"); };
  const leaveSaveExit = async () => { const ok = await save(true); if (ok) { setLeaveOpen(false); navigate("/editor"); } };
  const leaveDiscard = () => { setDirty(false); clearReminder(); setLeaveOpen(false); navigate("/editor"); };

  const preview = async () => {
    if (dirty) { const ok = await save(true); if (!ok) return; }
    window.open(`/preview/${id}`, "_blank");
  };

  const publish = async () => {
    const errs = validateAll();
    if (errs.length) { toast.error("לא ניתן לפרסם: " + errs[0]); return; }
    if (dirty) { const ok = await save(true); if (!ok) return; }
    try {
      const r = await api.post(`/pages/${id}/publish`);
      setPage(r.data); setDirty(false);
      toast.success("העמוד פורסם ועלה לאתר! 🎉");
    } catch (err) { toast.error(err.response?.data?.detail || "הפרסום נכשל"); }
  };

  const unpublish = async () => {
    try { const r = await api.post(`/pages/${id}/unpublish`); setPage(r.data); toast.success("העמוד הוסר מהאתר החי"); }
    catch { toast.error("הפעולה נכשלה"); }
  };

  const archivePage = async () => {
    try { const r = await api.post(`/pages/${id}/archive`); setPage(r.data); toast.success("העמוד הועבר לארכיון"); }
    catch { toast.error("הפעולה נכשלה"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/pages/${id}?confirm=true`);
      toast.success("העמוד נמחק");
      navigate("/editor");
    } catch (err) { toast.error(err.response?.data?.detail || "המחיקה נכשלה"); }
  };

  const openHistory = async () => {
    try { const r = await api.get(`/pages/${id}/versions`); setVersions(r.data); setHistOpen(true); }
    catch { toast.error("טעינת ההיסטוריה נכשלה"); }
  };

  const restore = async (versionId) => {
    try { const r = await api.post(`/pages/${id}/restore/${versionId}`); setPage(r.data); setDirty(false); setHistOpen(false); toast.success("שוחזר בהצלחה. אפשר לצפות בתצוגה מקדימה ולפרסם."); }
    catch { toast.error("השחזור נכשל"); }
  };

  if (!page) return <EditorLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#4A5D4E] border-t-transparent rounded-full animate-spin" /></div></EditorLayout>;

  return (
    <EditorLayout>
      <button onClick={goBack} data-testid="back-to-pages" className="flex items-center gap-1.5 text-sm text-[#5C635C] hover:text-[#1A1D1A] mb-5 transition-colors">
        <ArrowRight className="w-4 h-4" /> חזרה לכל העמודים
      </button>

      {/* Toolbar */}
      <div className="sticky top-4 z-20 bg-white border border-[#E5E2D9] rounded-2xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3 shadow-sm">
        <div className="min-w-0">
          <input data-testid="page-title-input" value={page.title} onChange={(e) => patch({ title: e.target.value })}
                 className="font-bold text-xl bg-transparent outline-none focus:bg-[#F7F5F0] rounded px-1 -mx-1 max-w-full" />
          <p className="text-xs text-[#8a8f89] px-1" dir="ltr">/{page.slug}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="save-draft-btn" variant="outline" onClick={() => save()} disabled={saving} className="rounded-full gap-1.5 border-[#E5E2D9]">
            <Save className="w-4 h-4" /> {saving ? "שומר..." : "שמירת טיוטה"}
          </Button>
          <Button data-testid="preview-btn" variant="outline" onClick={preview} className="rounded-full gap-1.5 border-[#E5E2D9]">
            <Eye className="w-4 h-4" /> תצוגה מקדימה
          </Button>
          <Button data-testid="publish-btn" onClick={publish} className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full gap-1.5">
            <Globe className="w-4 h-4" /> פרסום
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="more-menu-btn" className="w-9 h-9 rounded-full border border-[#E5E2D9] flex items-center justify-center hover:bg-[#F7F5F0]"><MoreVertical className="w-4 h-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" dir="rtl" className="font-editor">
              <DropdownMenuItem data-testid="menu-settings" onClick={() => setSettingsOpen(true)}><Settings2 className="w-4 h-4 ml-2" /> הגדרות עמוד ו-SEO</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-history" onClick={openHistory}><History className="w-4 h-4 ml-2" /> היסטוריית גרסאות</DropdownMenuItem>
              {page.status === "published" && <DropdownMenuItem data-testid="menu-unpublish" onClick={unpublish}><CloudOff className="w-4 h-4 ml-2" /> הסרה מהאתר החי</DropdownMenuItem>}
              <DropdownMenuItem data-testid="menu-archive" onClick={archivePage}><Archive className="w-4 h-4 ml-2" /> העברה לארכיון</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-delete" onClick={() => { setDelAck(false); setDelOpen(true); }} className="text-[#8C3A3A] focus:text-[#8C3A3A]"><Trash2 className="w-4 h-4 ml-2" /> מחיקת העמוד</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Blocks */}
      <div className="space-y-4">
        {page.blocks.length === 0 && (
          <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-[#E5E2D9]">
            <p className="text-[#5C635C] mb-4">העמוד ריק. הוסיפי בלוק ראשון כדי להתחיל.</p>
          </div>
        )}
        {page.blocks.map((block, i) => {
          const def = BLOCKS[block.type];
          const isOpen = expanded === i;
          const blockErrs = validateBlock(block);
          return (
            <div key={block.id} data-testid={`block-card-${i}`} className="bg-white border border-[#E5E2D9] rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 p-4">
                <GripVertical className="w-4 h-4 text-[#c8c4b8]" />
                <button onClick={() => setExpanded(isOpen ? -1 : i)} className="flex-1 text-right flex items-center gap-2">
                  <span className="font-semibold">{def?.label}</span>
                  {blockErrs.length > 0 && <AlertTriangle className="w-4 h-4 text-amber-500" title="חסר מידע" />}
                </button>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveBlock(i, -1)} data-testid={`block-up-${i}`} className="p-1.5 rounded hover:bg-[#F7F5F0] text-[#5C635C]"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => moveBlock(i, 1)} data-testid={`block-down-${i}`} className="p-1.5 rounded hover:bg-[#F7F5F0] text-[#5C635C]"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={() => setBlockDelIdx(i)} data-testid={`block-remove-${i}`} className="p-1.5 rounded hover:bg-red-50 text-[#8C3A3A]"><Trash2 className="w-4 h-4" /></button>
                  <button onClick={() => setExpanded(isOpen ? -1 : i)} data-testid={`block-toggle-${i}`} className="p-1.5 rounded hover:bg-[#F7F5F0] text-[#5C635C]">{isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
                </div>
              </div>
              {isOpen && <div className="px-5 pb-6 pt-1 border-t border-[#F0EEE6]"><BlockForm block={block} onChange={(nb) => setBlock(i, nb)} /></div>}
            </div>
          );
        })}

        <button onClick={() => setAddOpen(true)} data-testid="add-block-btn"
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#c8c4b8] rounded-2xl py-4 text-[#4A5D4E] font-semibold hover:border-[#4A5D4E] hover:bg-white transition-colors">
          <Plus className="w-5 h-5" /> הוספת בלוק
        </button>
      </div>

      {/* Add block dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent dir="rtl" className="font-editor max-w-2xl" data-testid="add-block-dialog">
          <DialogHeader><DialogTitle className="text-right">בחירת בלוק להוספה</DialogTitle>
            <DialogDescription className="text-right">כל בלוק הוא מקטע מוכן מראש. בחרי מה להוסיף לעמוד.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[62vh] overflow-y-auto space-y-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#4A5D4E] mb-3">בלוקים מעוצבים · EmotionalSignal</p>
              <div className="grid grid-cols-2 gap-3">
                {ES_BLOCK_ORDER.map((type) => {
                  const def = BLOCKS[type];
                  return (
                    <button key={type} onClick={() => addBlock(type)} data-testid={`add-block-${type}`}
                            className="text-right p-4 rounded-xl border border-[#4A5D4E]/30 bg-[#F2F5F1] hover:border-[#4A5D4E] transition-colors">
                      <p className="font-semibold text-sm mb-1">{def.label}</p>
                      <p className="text-xs text-[#5C635C] leading-snug">{def.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#8a8f89] mb-3">בלוקים בסיסיים</p>
              <div className="grid grid-cols-2 gap-3">
                {BLOCK_ORDER.map((type) => {
                  const def = BLOCKS[type];
                  return (
                    <button key={type} onClick={() => addBlock(type)} data-testid={`add-block-${type}`}
                            className="text-right p-4 rounded-xl border border-[#E5E2D9] hover:border-[#4A5D4E] hover:bg-[#F7F5F0] transition-colors">
                      <p className="font-semibold text-sm mb-1">{def.label}</p>
                      <p className="text-xs text-[#8a8f89] leading-snug">{def.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Page settings + SEO */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent dir="rtl" className="font-editor" data-testid="page-settings-dialog">
          <DialogHeader><DialogTitle className="text-right">הגדרות עמוד</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5">כתובת בקישור (slug)</label>
              <Input dir="ltr" data-testid="settings-slug" value={page.slug} onChange={(e) => patch({ slug: slugify(e.target.value) })} />
            </div>
            <label className="flex items-center gap-3 text-sm">
              <Switch data-testid="settings-show-nav" checked={page.show_in_nav} onCheckedChange={(v) => patch({ show_in_nav: v })} />
              להציג בתפריט הניווט של האתר
            </label>
            <div>
              <label className="block text-sm font-semibold mb-1.5">סדר בתפריט</label>
              <Input type="number" data-testid="settings-nav-order" value={page.nav_order} onChange={(e) => patch({ nav_order: parseInt(e.target.value) || 0 })} className="w-28" />
            </div>
            <div className="bg-[#F2F5F1] rounded-lg p-3 text-sm text-[#5C635C]">
              <p className="font-semibold text-[#4A5D4E] mb-1">מה זה SEO?</p>
              זה מה שגוגל מציג כשמישהו מוצא את העמוד שלך בחיפוש — כותרת ותיאור קצר. לא חובה, אבל עוזר שיגיעו אלייך.
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">כותרת לגוגל (SEO title)</label>
              <Input data-testid="seo-title" value={page.seo?.meta_title || ""} maxLength={70} onChange={(e) => patch({ seo: { ...page.seo, meta_title: e.target.value } })} />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">תיאור לגוגל (SEO description)</label>
              <Textarea data-testid="seo-desc" value={page.seo?.meta_description || ""} maxLength={160} rows={3} onChange={(e) => patch({ seo: { ...page.seo, meta_description: e.target.value } })} />
            </div>
          </div>
          <DialogFooter><Button onClick={() => { setSettingsOpen(false); }} className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full">סגירה</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete double-confirmation */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent dir="rtl" className="font-editor" data-testid="delete-page-dialog">
          <DialogHeader>
            <DialogTitle className="text-right flex items-center gap-2 text-[#8C3A3A]"><AlertTriangle className="w-5 h-5" /> מחיקת העמוד "{page.title}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#5C635C]">
            <p className="bg-red-50 border border-red-100 rounded-lg p-3 text-[#8C3A3A]">
              פעולה זו תסיר את העמוד מהאתר החי ומרשימת העמודים. לא ניתן יהיה לגשת אליו יותר.
            </p>
            <p>אם את רק רוצה להוריד אותו זמנית מהאתר — עדיף להשתמש ב<b>"הסרה מהאתר החי"</b> או ב<b>"ארכיון"</b> במקום מחיקה.</p>
            <label className="flex items-start gap-2 pt-2">
              <input type="checkbox" data-testid="delete-ack" checked={delAck} onChange={(e) => setDelAck(e.target.checked)} className="accent-[#8C3A3A] w-4 h-4 mt-0.5" />
              <span>אני מבינה שהעמוד יימחק ולא ניתן יהיה לשחזר אותו בקלות.</span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelOpen(false)} className="rounded-full">ביטול</Button>
            <Button data-testid="delete-confirm" onClick={doDelete} disabled={!delAck} className="bg-[#8C3A3A] hover:bg-[#742f2f] rounded-full disabled:opacity-40">מחיקה סופית</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version history */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent dir="rtl" className="font-editor max-w-lg" data-testid="history-dialog">
          <DialogHeader><DialogTitle className="text-right flex items-center gap-2"><History className="w-5 h-5" /> היסטוריית גרסאות</DialogTitle>
            <DialogDescription className="text-right">כל פרסום נשמר כגרסה. אפשר לחזור לכל גרסה קודמת.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {versions.length === 0 ? (
              <p className="text-center text-[#8a8f89] py-8">עדיין אין גרסאות שמורות. גרסה נשמרת בכל פרסום.</p>
            ) : versions.map((v) => (
              <div key={v.id} data-testid={`version-${v.id}`} className="flex items-center justify-between border border-[#E5E2D9] rounded-xl p-3">
                <div>
                  <p className="text-sm font-semibold">{v.type === "publish" ? "פרסום" : "גיבוי אוטומטי"} · {v.title}</p>
                  <p className="text-xs text-[#8a8f89]">{new Date(v.created_at).toLocaleString("he-IL")}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => restore(v.id)} data-testid={`restore-${v.id}`} className="rounded-full gap-1.5 border-[#E5E2D9]">
                  <RotateCcw className="w-3.5 h-3.5" /> שחזור
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Block delete confirmation */}
      <Dialog open={blockDelIdx !== null} onOpenChange={(o) => !o && setBlockDelIdx(null)}>
        <DialogContent dir="rtl" className="font-editor" data-testid="block-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-right flex items-center gap-2 text-[#8C3A3A]"><AlertTriangle className="w-5 h-5" /> הסרת בלוק</DialogTitle>
            <DialogDescription className="text-right">
              {blockDelIdx !== null && page.blocks[blockDelIdx] ? `הבלוק "${BLOCKS[page.blocks[blockDelIdx].type]?.label}" יוסר מהעמוד. השינוי ייכנס לתוקף באתר רק לאחר פרסום.` : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBlockDelIdx(null)} className="rounded-full">ביטול</Button>
            <Button data-testid="block-delete-confirm" onClick={() => { removeBlock(blockDelIdx); setBlockDelIdx(null); }} className="bg-[#8C3A3A] hover:bg-[#742f2f] rounded-full">הסרת הבלוק</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes — leave guard */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent dir="rtl" className="font-editor" data-testid="leave-guard-dialog">
          <DialogHeader>
            <DialogTitle className="text-right flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> יש שינויים שלא נשמרו</DialogTitle>
            <DialogDescription className="text-right">ביצעת שינויים בעמוד שעדיין לא נשמרו. מה לעשות לפני היציאה?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setLeaveOpen(false)} className="rounded-full">המשך עריכה</Button>
            <Button variant="outline" onClick={leaveDiscard} data-testid="leave-discard" className="rounded-full border-[#8C3A3A] text-[#8C3A3A] hover:bg-red-50">יציאה בלי לשמור</Button>
            <Button onClick={leaveSaveExit} data-testid="leave-save-exit" className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full">שמירה ויציאה</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2-minute save reminder */}
      <Dialog open={saveReminderOpen} onOpenChange={setSaveReminderOpen}>
        <DialogContent dir="rtl" className="font-editor" data-testid="save-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="text-right flex items-center gap-2"><Save className="w-5 h-5 text-[#4A5D4E]" /> לשמור את השינויים?</DialogTitle>
            <DialogDescription className="text-right">עברו כשתי דקות מאז השינוי האחרון ועדיין לא שמרת. כדאי לשמור טיוטה כדי לא לאבד את העבודה.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setSaveReminderOpen(false); scheduleReminder(); }} data-testid="reminder-later" className="rounded-full">אזכיר לי אחר כך</Button>
            <Button data-testid="reminder-save" onClick={() => save()} disabled={saving} className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full gap-1.5"><Save className="w-4 h-4" /> {saving ? "שומר..." : "שמירת טיוטה"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </EditorLayout>
  );
}
